import { useEffect, useState } from 'preact/hooks';
import { adminCall } from './api';

export const INTELLIGENCE_SERVICE = 'momentpix.admin.v1.PlatformIntelligenceService';
type Count = number | string;
interface Activity {
  orgId?: string; orgName?: string; views?: Count; visitors?: Count; orders?: Count;
  revenueCents?: Count; botViews?: Count; crawlerViews?: Count; automatedRatio?: number;
}
interface Signal {
  id: string; orgId?: string; orgName?: string; rawIp?: string; kind: string; count: Count;
  action: string; firstSeen: string; lastSeen: string; clearedAt?: string;
}
interface Job { name: string; state: string; lastRun?: string; rowsAffected?: Count; measure: string; detail?: string }
const count = (n?: Count) => Number(n || 0).toLocaleString();
const date = (s?: string) => s ? new Date(s).toLocaleString() : '—';
const ratio = (n?: number) => `${((n || 0) * 100).toFixed(1)}%`;
const revenue = (n?: Count) => (Number(n || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function IntelligencePage({ canClear }: { canClear: boolean }) {
  const [days, setDays] = useState(30);
  const [activity, setActivity] = useState<{ totals?: Activity; organizations?: Activity[]; windowFrom?: string; windowTo?: string } | null>(null);
  const [retention, setRetention] = useState<{ jobs?: Job[]; source?: string } | null>(null);
  const [signals, setSignals] = useState<Signal[]>([]);
  const [orgId, setOrgId] = useState('');
  const [includeCleared, setIncludeCleared] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [clearing, setClearing] = useState('');

  useEffect(() => {
    let current = true;
    setBusy(true); setError(''); setActivity(null); setRetention(null); setSignals([]);
    Promise.all([
      adminCall(INTELLIGENCE_SERVICE, 'GetCustomerActivity', { rangeDays: days }),
      adminCall(INTELLIGENCE_SERVICE, 'GetRetentionStatus'),
      adminCall(INTELLIGENCE_SERVICE, 'ListSecuritySignals', { rangeDays: days, orgId, includeCleared, offset, limit: 50 }),
    ]).then(([a, r, s]) => {
      if (current) { setActivity(a); setRetention(r); setSignals(s.signals || []); setHasMore(!!s.hasMore); }
    }).catch(e => { if (current) setError(e.message || 'Unable to load customer intelligence'); })
      .finally(() => { if (current) setBusy(false); });
    return () => { current = false; };
  }, [days, orgId, includeCleared, offset, refresh]);

  async function clear(signal: Signal) {
    setClearing(signal.id); setError('');
    try {
      await adminCall(INTELLIGENCE_SERVICE, 'ClearSecuritySignal', { id: signal.id });
      setRefresh(n => n + 1);
    } catch (e: any) { setError(e.message || 'Unable to clear signal'); }
    finally { setClearing(''); }
  }

  const totals = activity?.totals;
  return <div style={{ padding: 24, maxWidth: 1400 }}>
    <h1>Customer intelligence</h1>
    <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 }}>
      <label>Period <select value={days} onChange={e => { setDays(Number(e.currentTarget.value)); setOffset(0); }}>
        {[7, 30, 90].map(n => <option key={n} value={n}>{n} days</option>)}
      </select></label>
      <button disabled={busy || !!clearing} onClick={() => setRefresh(n => n + 1)}>Refresh</button>
      {busy && <span role="status">Loading…</span>}
    </div>
    {error && <p role="alert" class="error">{error}</p>}
    {activity && <>
      <p>{date(activity.windowFrom)} – {date(activity.windowTo)}</p>
      <div class="intel-totals">
        {[
          ['Gallery views', count(totals?.views)], ['Unique visitors', count(totals?.visitors)],
          ['Paid orders', count(totals?.orders)], ['Revenue', revenue(totals?.revenueCents)],
          ['Bot / crawler share', ratio(totals?.automatedRatio)],
        ].map(([label, value]) => <div class="intel-tile" key={label}><div>{label}</div><strong>{value}</strong></div>)}
      </div>
      <p>Views count customer gallery page opens. Visitors are distinct browsers that opened a gallery page or photo; the platform total deduplicates browsers across organizations. Staff and automation are excluded. Paid orders are completed or fulfilled orders created in this period; revenue uses their order amounts. Automation share is bot and verified crawler gallery page views divided by all customer, bot and crawler gallery page views.</p>
      <h2>Organization activity</h2>
      <div class="intel-scroll"><table class="intel-table"><thead><tr>
        {['Organization', 'Views', 'Visitors', 'Paid orders', 'Revenue', 'Bot views', 'Crawler views', 'Automation'].map(h => <th key={h}>{h}</th>)}
      </tr></thead><tbody>{(activity.organizations || []).map(a => <tr key={a.orgId}>
        <td>{a.orgName}</td><td>{count(a.views)}</td><td>{count(a.visitors)}</td><td>{count(a.orders)}</td><td>{revenue(a.revenueCents)}</td><td>{count(a.botViews)}</td><td>{count(a.crawlerViews)}</td><td>{ratio(a.automatedRatio)}</td>
      </tr>)}</tbody></table></div>
    </>}
    <h2>Abuse signals</h2>
    <p>Raw security IPs are visible only to platform staff and expire after 90 days. Clearing acknowledges a signal and records an audit entry; it does not change detection or enforcement.</p>
    <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
      <label>Organization <select value={orgId} onChange={e => { setOrgId(e.currentTarget.value); setOffset(0); }}>
        <option value="">All, including unattributed</option>
        {(activity?.organizations || []).map(a => <option key={a.orgId} value={a.orgId}>{a.orgName}</option>)}
      </select></label>
      <label><input type="checkbox" checked={includeCleared} onChange={e => { setIncludeCleared(e.currentTarget.checked); setOffset(0); }} /> Include cleared</label>
    </div>
    {!busy && !error && signals.length === 0 && <p>No signals in this period.</p>}
    {signals.length > 0 && <div class="intel-scroll"><table class="intel-table"><thead><tr>
      {['Organization', 'IP', 'Kind', 'Count', 'Action', 'Last seen', 'State'].map(h => <th key={h}>{h}</th>)}
    </tr></thead><tbody>{signals.map(s => <tr key={s.id}>
      <td>{s.orgName || s.orgId || 'Unattributed'}</td><td>{s.rawIp || 'Expired or erased'}</td><td>{s.kind}</td><td>{count(s.count)}</td><td>{s.action}</td><td title={`First seen ${date(s.firstSeen)}`}>{date(s.lastSeen)}</td>
      <td>{s.clearedAt ? `Cleared ${date(s.clearedAt)}` : <><span>Open </span>{canClear && <button disabled={!!clearing || busy} onClick={() => clear(s)}>{clearing === s.id ? 'Clearing…' : 'Clear'}</button>}</>}</td>
    </tr>)}</tbody></table></div>}
    <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
      <button disabled={busy || offset === 0} onClick={() => setOffset(n => Math.max(0, n - 50))}>Previous</button>
      <button disabled={busy || !hasMore} onClick={() => setOffset(n => n + 50)}>Next</button>
    </div>
    <h2>Retention and erasure</h2>
    {retention && <><p>{retention.source}</p><div class="intel-scroll"><table class="intel-table"><thead><tr>
      {['Job', 'State', 'Last run / completion', 'Count', 'Measure', 'Details'].map(h => <th key={h}>{h}</th>)}
    </tr></thead><tbody>{(retention.jobs || []).map(j => <tr key={j.name}>
      <td>{j.name}</td><td>{j.state}</td><td>{date(j.lastRun)}</td><td>{j.lastRun ? count(j.rowsAffected) : '—'}</td><td>{j.measure}</td><td>{j.detail}</td>
    </tr>)}</tbody></table></div></>}
  </div>;
}
